import React, { useState, useEffect } from 'react';
import { Modal } from '../../common/Modal';
import { Guide } from '../../../types/guide';
import { useGuidesStore } from '../../../stores/guidesStore';
import { usePilgrimGuideStore } from '../../../stores/pilgrimGuideStore';
import { Search, User } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { collection, query, where, getDocs, writeBatch, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  linkId: string;
  currentGuideId: string;
  pilgrimId: string;
  pilgrimName: string;
};

export const TransferGuideModal = ({
  isOpen,
  onClose,
  linkId,
  currentGuideId,
  pilgrimId,
  pilgrimName
}: Props) => {
  const { translations } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuideId, setSelectedGuideId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { assignPendingLinkToGuide } = usePilgrimGuideStore();
  const { guides, initialized, initializeListener } = useGuidesStore();
  const userId = localStorage.getItem('userId');
  const userRole = localStorage.getItem('userRole');

  // Initialize guides data when component mounts
  useEffect(() => {
    if (!initialized) {
      initializeListener();
    }
  }, [initialized, initializeListener]);

  // Filter guides based on user role and search term
  const filteredGuides = Object.values(guides)
    .filter(guide => {
      // Don't show current guide
      if (guide.id === currentGuideId) return false;

      // For organizer, only show their guides
      if (userRole === 'organizer' && userId) {
        return guide.organizerId === userId;
      }

      // For admin, show all guides
      const matchesSearch = !searchTerm || 
        guide.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.email.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleTransfer = async () => {
    if (!selectedGuideId) {
      toast.error(translations?.transferGuide?.selectGuide || 'Please select a guide');
      return;
    }

    setIsLoading(true);
    try {
      if (!currentGuideId) {
        await assignPendingLinkToGuide(linkId, pilgrimId, selectedGuideId, translations);
        toast.success(translations?.transferGuide?.success || 'Pilgrim transferred successfully');
        onClose();
        return;
      }

      const batch = writeBatch(firestore);

      // Get the selected guide's data
      const selectedGuide = guides[selectedGuideId];
      if (!selectedGuide) throw new Error('Selected guide not found');
      const organizerDoc = await getDoc(doc(firestore, 'Organizers', selectedGuide.organizerId));
      const organizerName = organizerDoc.exists() ? organizerDoc.data().name : 'Unknown Organizer';

      // Update the link
      const linkRef = doc(firestore, 'PilgrimGuideLinks', linkId);
      batch.update(linkRef, {
        guideId: selectedGuideId,
        guideName: selectedGuide.name,
        organizerId: selectedGuide.organizerId,
        organizerName,
        updatedAt: Date.now()
      });

      // Update ownership if exists
      const ownershipRef = collection(firestore, 'PilgrimOwnership');
      const ownershipQuery = query(ownershipRef, where('pilgrimId', '==', pilgrimId));
      const ownershipSnapshot = await getDocs(ownershipQuery);

      if (!ownershipSnapshot.empty) {
        const ownershipDoc = ownershipSnapshot.docs[0];
        batch.update(ownershipDoc.ref, {
          realGuideId: selectedGuideId,
          ownerGuideId: selectedGuideId,
          updatedAt: Date.now()
        });
      }

      await batch.commit();
      toast.success(translations?.transferGuide?.success || 'Pilgrim transferred successfully');
      onClose();
    } catch (error) {
      console.error('Error transferring pilgrim:', error);
      toast.error(translations?.transferGuide?.error || 'Failed to transfer pilgrim');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.transferGuide?.title || `Transfer ${pilgrimName} to Another Guide`}
    >
      <div className="space-y-6">
        {/* Pilgrim Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium mb-2">
            {translations?.transferGuide?.pilgrimInfo || 'Pilgrim Information'}
          </h3>
          <p className="text-gray-600">{pilgrimName}</p>
        </div>

        {/* Guide Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.transferGuide?.selectGuide || 'Select New Guide'}
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={translations?.transferGuide?.searchPlaceholder || 'Search guides...'}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>
        </div>

        {/* Guides List */}
        <div className="border rounded-lg max-h-60 overflow-y-auto">
          {filteredGuides.length > 0 ? (
            filteredGuides.map((guide) => (
              <button
                key={guide.id}
                onClick={() => setSelectedGuideId(guide.id)}
                className={`w-full p-4 text-right hover:bg-gray-50 transition-colors border-b last:border-b-0 flex items-center justify-between ${
                  selectedGuideId === guide.id ? 'bg-primary-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary-50">
                    <User className="text-primary-500" size={20} />
                  </div>
                  <div>
                    <div className="font-medium">{guide.name}</div>
                    <div className="text-sm text-gray-500">{guide.email}</div>
                  </div>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 ${
                  selectedGuideId === guide.id 
                    ? 'border-primary-500 bg-primary-500' 
                    : 'border-gray-300'
                }`} />
              </button>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">
              {translations?.transferGuide?.noGuides || 'No guides found'}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.transferGuide?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleTransfer}
            disabled={isLoading || !selectedGuideId}
            className={`px-6 py-2 rounded-lg text-white transition-colors ${
              isLoading || !selectedGuideId
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {isLoading 
              ? translations?.transferGuide?.transferring || 'Transferring...'
              : translations?.transferGuide?.transfer || 'Transfer'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
